<template>
  <div class="page-content">
    <div class="title">
      Price Groups Management
    </div>
    <div id="new-form" class="list" v-if="newPriceGroupForm">
      <form @submit.prevent="createEntry">
        <div class="input-box new">
          <div class="field key">
            <input
              type="text"
              class="regular bordered"
              v-model="newEntry.name"
              placeholder="Price group name"
              required
            />
          </div>
          <div class="field value">
            <input
              class="regular bordered"
              type="number"
              step="0.0001"
              v-model="newEntry.retail"
              required
            />
          </div>
          <div class="field value">
            <input
              class="regular bordered"
              type="number"
              step="0.0001"
              v-model="newEntry.repairShop"
              required
            />
          </div>
          <div class="field value">
            <input
              class="regular bordered"
              type="number"
              step="0.0001"
              v-model="newEntry.wholesale"
              required
            />
          </div>
          <div class="field actions">
            <template v-if="keySaving == newEntry.name && keySaving !== ''">
              Saving ...
            </template>
            <template v-else>
              <a class="btn btn-success-button" @click="createEntry()"
                >Create</a
              >
              <a class="btn btn-dark-button" @click="discardNewEntry()"
                >Discard</a
              >
            </template>
          </div>
        </div>
      </form>
    </div>
    <button
      v-if="!newPriceGroupForm"
      @click="initNewEntry()"
      class="btn btn-dark-button w-10 h-10 flex justify-center"
    >
      +
    </button>
    <div class="mt-2 mb-6" v-if="!newPriceGroupForm">
      <button
        @click="showCalculator = !showCalculator"
        :class="{
          'text-green-success': !showCalculator,
          'text-red-cancel': showCalculator
        }"
      >
        {{ showCalculator ? "Hide Calculator" : "show calculator" }}
      </button>
      <template v-if="showCalculator">
        <PriceGroupCalculator :priceGroups="formEntries"></PriceGroupCalculator>
      </template>
    </div>
    <div class="list" v-if="!newPriceGroupForm">
      <div
        class="input-box"
        v-for="entrie in formEntries"
        :key="entrie.id + '-' + i"
      >
        <template v-if="keySaving != entrie.key">
          <div class="field key">
            {{ entrie.name }}
          </div>
          <div class="field value">
            <input
              class="regular bordered"
              type="number"
              step="0.0001"
              v-model="entrie.retail"
            />
          </div>
          <div class="field value">
            <input
              class="regular bordered"
              type="number"
              step="0.0001"
              v-model="entrie.repairShop"
            />
          </div>
          <div class="field value">
            <input
              class="regular bordered"
              type="number"
              step="0.0001"
              v-model="entrie.wholesale"
            />
          </div>
          <div class="field actions">
            <template v-if="keySaving == entrie.name">
              Saving ...
            </template>
            <template v-else>
              <a
                class="btn btn-success-button"
                v-if="edited(entrie.name)"
                @click="updateEntrie(entrie)"
                >Update</a
              >
              <a
                class="btn btn-dark-button"
                v-if="edited(entrie.name)"
                @click="discard(entrie.name)"
                >Discard</a
              >
              <button
                v-if="!edited(entrie.name)"
                class="w-6 h-6 my-auto"
                @click="deleteGroup(entrie)"
              >
                <img src="@/assets/icons/trash.svg" alt="delete" />
              </button>
            </template>
          </div>
        </template>
        <template v-else>
          <div class="loading">
            <BaseSmallSpinner />
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import {
  loadPriceGroups as loadPriceGroupsApi,
  updatePriceGroup as updatePriceGroupApi,
  createPriceGroup as createPriceGroupApi,
  deletePriceGroup as deletePriceGroupApi
} from "@/api/store/PriceGroupsService.js";
import PriceGroupCalculator from "@/components/product/PriceGroupCalculator.vue";

export default {
  name: "PriceGroups",
  components: { PriceGroupCalculator },
  data() {
    return {
      newEntry: null,
      formEntries: [],
      originalValues: [],
      keySaving: "",
      showFileSelector: false,
      fileSubcategory: null,
      newPriceGroupForm: false,
      i: 1,
      showCalculator: false
    };
  },
  mounted() {
    this.loadPriceGroups();
  },
  computed: {
    currentUser() {
      return this.$store.getters["auth/currentUser"];
    },
    edited() {
      return name => {
        let original = this.originalValues.find(x => {
          return x.name == name;
        });
        let current = this.formEntries.find(x => {
          return x.name == name;
        });
        return !(JSON.stringify(original) == JSON.stringify(current));
      };
    }
  },
  methods: {
    loadPriceGroups() {
      loadPriceGroupsApi().then(res => {
        this.originalValues = JSON.parse(JSON.stringify(res));
        this.formEntries = JSON.parse(JSON.stringify(res));
      });
    },
    discard(name) {
      let original = this.originalValues.find(x => {
        return x.name == name;
      });
      let currentIndex = this.formEntries.findIndex(x => {
        return x.name == name;
      });
      this.formEntries[currentIndex].retail = original.retail;
      this.formEntries[currentIndex].repairShop = original.repairShop;
      this.formEntries[currentIndex].wholesale = original.wholesale;
    },
    async updateEntrie(entrie) {
      this.keySaving = entrie.name;
      try {
        await updatePriceGroupApi(
          entrie.name,
          entrie.retail,
          entrie.repairShop,
          entrie.wholesale
        );
        let orig = this.originalValues.findIndex(x => {
          return x.name == entrie.name;
        });
        this.originalValues[orig] = JSON.parse(JSON.stringify(entrie));
        this.$toast.open({
          message: `Price group update`,
          type: "success",
          position: "top-right"
        });
      } catch (e) {
        this.$toast.open({
          message: `Error saving price group`,
          type: "error",
          position: "top-right"
        });
      }
      this.keySaving = "";
    },
    async createEntry() {
      if (this.newEntry.name === "") {
        this.$toast.open({
          message: `The name is required`,
          type: "error",
          position: "top-right"
        });
      } else {
        this.keySaving = this.newEntry.name;
        try {
          await createPriceGroupApi(
            this.newEntry.name,
            this.newEntry.retail,
            this.newEntry.repairShop,
            this.newEntry.wholesale
          );
          const inserted = {
            name: this.newEntry.name,
            retail: this.newEntry.retail,
            repairShop: this.newEntry.repairShop,
            wholesale: this.newEntry.wholesale
          };
          this.originalValues.push({ ...inserted });
          this.formEntries.push({ ...inserted });
          this.newPriceGroupForm = false;
          this.$toast.open({
            message: `Price group created`,
            type: "success",
            position: "top-right"
          });
        } catch (e) {
          console.log(e);
          this.$toast.open({
            message: `Error Creating price group`,
            type: "error",
            position: "top-right"
          });
        } finally {
          this.keySaving = false;
        }
      }
    },
    discardNewEntry() {
      this.newPriceGroupForm = false;
      this.newEntry = {
        name: "",
        retail: 0,
        repairShop: 0,
        wholesale: 0
      };
    },
    initNewEntry() {
      this.newEntry = {
        name: "",
        retail: 0,
        repairShop: 0,
        wholesale: 0
      };
      this.newPriceGroupForm = true;
    },
    async deleteGroup(entry) {
      if (confirm(`please confirm to delete price group ${entry.name}`)) {
        try {
          await deletePriceGroupApi(entry.id);
          this.$toast.open({
            message: `Price group created`,
            type: "success",
            position: "top-right"
          });
          let orig = this.formEntries.findIndex(x => {
            return x.id == entry.id;
          });
          this.formEntries.splice(orig, 1);
        } catch (e) {
          this.$toast.open({
            message: `Error deleting price group`,
            type: "error",
            position: "top-right"
          });
        }
      }
    }
  }
};
</script>
<style lang="scss" scoped>
@import "@/assets/style/css/generic-variables.scss";

.input-box {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr 150px;
  margin: 10px 0px;
  border: 1px solid grey;
  padding: 10px 5px;
  border-radius: 4px;
  min-height: 60px;
  .field {
    position: relative;
    padding: 5px;
    input,
    textarea {
      width: 100%;
      height: 100%;
    }
    input,
    select {
      width: 100%;
    }
    .img-value {
      max-height: 60px;
      margin: 0 auto;
    }
    .edit-icon {
      width: 20px;
      position: absolute;
      top: 20px;
      right: 20px;
    }
  }
  .actions {
    display: flex;
    flex-direction: row;
    a {
      margin: 1px 0px;
    }
  }
}
.input-box:hover {
  background-color: $tableTRHoverBG;
  color: $tableTRHoverText;
}
</style>
